import { useEffect, useId, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@comforthub/design-system'
import { api } from '../../../src/lib/api'
import { ORDER_PAYMENT_STATUSES } from '../../../src/lib/optionSets'
import { useSearchParamState } from '../../../src/lib/useSearchParamState'
import { apiErrorMessage } from '../../../src/lib/apiError'
import Pagination from '../../../src/components/Pagination'
import {
  Search, Plus, Edit2, User, Store, Clock,
  AlertTriangle, Loader2, SlidersHorizontal
} from 'lucide-react'
import { useContainerWidth } from '@comforthub/contracts'
import {
  TOKENS, Button, ConfirmDialog, DataTable, EmptyState, ErrorState, formatMoney,
  type DataTableColumn,
} from '@comforthub/design-system'
import CreateOrderModal, { type ManualOrder, toLocalISOString } from './CreateOrderModal'
import {
  calendarHrefForAppointment,
  formatOrderAppointmentLine,
  formatOrderTargetLine,
  isOrderOverdue,
} from './orderTargetDisplay'
import { PaymentStatusBadge } from './PaymentStatusBadge'
import { OrderStatusBadge } from './OrderStatusBadge'
import { ORDER_STATUSES } from './orderStatusDisplay'
import { isCancelledOrder } from './orderState'
import { SubscriptionOrderChip } from './SubscriptionOrderChip'

interface Worker {
  id: string
  name: string
}

interface StoreItem {
  id: string
  name: string
  isDeleted?: boolean
}

/** A Spring `Page`, as GET /orders returns it. */
interface PageResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  /** The page's index, from 0. */
  number: number
  first: boolean
  last: boolean
}

/** Orders per page on the List. */
const PAGE_SIZE = 50

/** Where the table turns into cards: the kit DataTable's own breakpoint, Tailwind's max-sm. */
const PHONE_QUERY = '(max-width: 639px)'

/** True while the media query matches. False where matchMedia doesn't exist, as in jsdom. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  )
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const list = window.matchMedia(query)
    const onChange = () => setMatches(list.matches)
    onChange()
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])
  return matches
}

const WORKFLOW_STATUSES: { value: string; label: string }[] = ORDER_STATUSES.map(({ status, label }) => ({
  value: status,
  label,
}))

function localInputToIso(local: string): string | undefined {
  if (!local) return undefined
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toISOString()
}

/** "#A, #B and #C" — or the first five, then "and 3 more". */
function listOrderNumbers(orders: ManualOrder[], max = 5): string {
  const numbers = orders.map(o => o.orderNr || o.id)
  if (numbers.length <= 1) return numbers.join('')
  if (numbers.length > max) return `${numbers.slice(0, max).join(', ')} and ${numbers.length - max} more`
  return `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`
}

export interface OrdersWidgetProps {
  /** When false, hide create/edit actions. Defaults to true for embedded dashboard widgets. */
  canWriteOrders?: boolean
  /**
   * Keep the search and filters in the page's URL, so Back and a reload keep
   * them. Only the List page sets this; on the Dashboard they stay in the widget.
   */
  urlState?: boolean
}

/** How long typing has to pause before the search goes into the URL. */
const SEARCH_URL_DELAY_MS = 300

export default function OrdersWidget({ canWriteOrders = true, urlState = false }: OrdersWidgetProps) {
  const [containerRef, width] = useContainerWidth()
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // Filters and the page are written with `replace`, so Back leaves the List
  // rather than stepping through every change. The selection stays out of the URL.
  const inUrl = { enabled: urlState }
  // The page as people count it, from 1: `?page=2` is the second page.
  const [pageParam, setPageParam] = useSearchParamState('page', '1', inUrl)
  const pageIndex = Math.max((Number.parseInt(pageParam, 10) || 1) - 1, 0)
  /** A filter that changes goes back to the first page. */
  const firstPageOn = (set: (next: string) => void) => (next: string) => {
    set(next)
    setPageParam('1')
  }
  const [searchQuery, setSearchQueryParam] = useSearchParamState('q', '', inUrl)
  const setSearchQuery = firstPageOn(setSearchQueryParam)
  const [searchInput, setSearchInput] = useState(searchQuery)
  const [filterWorker, setFilterWorkerParam] = useSearchParamState('staff', '', inUrl)
  const setFilterWorker = firstPageOn(setFilterWorkerParam)
  const [filterStore, setFilterStoreParam] = useSearchParamState('store', '', inUrl)
  const setFilterStore = firstPageOn(setFilterStoreParam)
  const [filterStatus, setFilterStatusParam] = useSearchParamState('status', '', inUrl)
  const setFilterStatus = firstPageOn(setFilterStatusParam)
  const [filterPayment, setFilterPaymentParam] = useSearchParamState('pay', '', inUrl)
  const setFilterPayment = firstPageOn(setFilterPaymentParam)
  // `datetime-local` values, as the inputs give them (2026-09-26T14:00).
  const [filterReadyFrom, setFilterReadyFromParam] = useSearchParamState('readyFrom', '', inUrl)
  const setFilterReadyFrom = firstPageOn(setFilterReadyFromParam)
  const [filterReadyTo, setFilterReadyToParam] = useSearchParamState('readyTo', '', inUrl)
  const setFilterReadyTo = firstPageOn(setFilterReadyToParam)
  const [appointmentsParam, setAppointmentsParam] = useSearchParamState('appointments', '', inUrl)
  const hideCalendarBound = appointmentsParam !== 'show'
  const setHideCalendarBound = (hide: boolean) => {
    setAppointmentsParam(hide ? '' : 'show')
    setPageParam('1')
  }

  // The search goes into the URL (and to the server) once typing pauses.
  useEffect(() => {
    if (searchInput === searchQuery) return
    const timer = window.setTimeout(() => setSearchQuery(searchInput), SEARCH_URL_DELAY_MS)
    return () => window.clearTimeout(timer)
    // Only typing starts the timer; a URL change is copied into the box below.
  }, [searchInput])

  // Back or a link changes the URL's search: show it in the box.
  useEffect(() => {
    setSearchInput(searchQuery)
  }, [searchQuery])
  // Narrow screens only: the filter panel starts closed and is not remembered.
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filtersPanelId = useId()

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkStatus, setBulkStatus] = useState('completed')
  const [bulkWorker, setBulkWorker] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkMessage, setBulkMessage] = useState<string | null>(null)
  const [confirmBulkCancel, setConfirmBulkCancel] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingOrder, setEditingOrder] = useState<ManualOrder | null>(null)

  const { data: workers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get<{ content: Worker[] }>('/users', { params: { size: 200 } })
      return res.data.content || []
    }
  })

  const { data: storesRes } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await api.get<{ content: StoreItem[] }>('/stores', { params: { size: 100 } })
      return (res.data.content || []).filter(s => !s.isDeleted)
    }
  })
  const stores = storesRes || []

  const filterKey = [
    searchQuery,
    filterWorker,
    filterStore,
    filterStatus,
    filterPayment,
    filterReadyFrom,
    filterReadyTo,
    hideCalendarBound,
  ]

  const {
    data: ordersPage,
    isLoading: ordersLoading,
    isError: ordersFailed,
    isFetching: ordersFetching,
    isPlaceholderData: showingPreviousPage,
    error: ordersError,
    refetch: refetchOrders,
  } = useQuery({
    queryKey: ['orders', ...filterKey, pageIndex],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {
        page: pageIndex,
        size: PAGE_SIZE,
      }
      if (filterWorker) params.assignedTo = filterWorker
      if (filterStore) params.storeId = filterStore
      if (filterStatus) params.status = filterStatus
      if (filterPayment) params.paymentStatus = filterPayment
      const fromIso = localInputToIso(filterReadyFrom)
      const toIso = localInputToIso(filterReadyTo)
      // Filters on the pickup time. readyBy* are the older names of the same filter.
      if (fromIso) {
        params.pickupFrom = fromIso
        params.readyByFrom = fromIso
      }
      if (toIso) {
        params.pickupTo = toIso
        params.readyByTo = toIso
      }
      // Visits (booked services) are left out by the server, so the total counts only what shows.
      if (hideCalendarBound) params.hideAppointments = true

      const query = searchQuery.trim()
      if (query) {
        // An order number is six hex characters, with or without the "#"
        // ("B17C5C" or "#B17C5C"); anything else is a customer name.
        if (/^#?[0-9a-fA-F]{6}$/.test(query)) {
          params.orderNr = query
        } else {
          params.customer = query
        }
      }

      const res = await api.get<PageResponse<ManualOrder>>('/orders', { params })
      return res.data
    },
    // Paging keeps the current rows on screen, dimmed, until the next page arrives.
    placeholderData: keepPreviousData,
  })
  const orders = useMemo(() => ordersPage?.content ?? [], [ordersPage])
  const totalElements = ordersPage?.totalElements ?? orders.length
  const totalPages = ordersPage?.totalPages ?? 1

  // A page past the end (an old link, or the last orders on it were cancelled)
  // goes to the last page there is.
  useEffect(() => {
    if (!ordersPage || showingPreviousPage) return
    const lastIndex = Math.max(totalPages - 1, 0)
    if (pageIndex > lastIndex) setPageParam(String(lastIndex + 1))
  }, [ordersPage, showingPreviousPage, totalPages, pageIndex])

  // "Select all on page" means this page: a new page or new filters start with nothing selected.
  const listKey = JSON.stringify([...filterKey, pageIndex])
  useEffect(() => {
    setSelectedIds(prev => (prev.size === 0 ? prev : new Set()))
  }, [listKey])

  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev
      const visible = new Set(orders.map(o => o.id))
      let changed = false
      const next = new Set<string>()
      prev.forEach(id => {
        if (visible.has(id)) next.add(id)
        else changed = true
      })
      if (!changed && next.size === prev.size) return prev
      return next
    })
  }, [orders])

  const allVisibleSelected = orders.length > 0 && orders.every(o => selectedIds.has(o.id))

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set())
      return
    }
    setSelectedIds(new Set(orders.map(o => o.id)))
  }

  const toggleOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const runBulk = async (
    label: string,
    action: (id: string) => Promise<unknown>,
  ) => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    setBulkBusy(true)
    setBulkMessage(null)
    setError(null)
    let failed = 0
    for (const id of ids) {
      try {
        await action(id)
      } catch {
        failed += 1
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['orders'] })
    setBulkBusy(false)
    if (failed === 0) {
      setBulkMessage(`${label}: ${ids.length} order(s) updated`)
      setSelectedIds(new Set())
    } else {
      setBulkMessage(`${label}: ${ids.length - failed} ok, ${failed} failed`)
    }
  }

  const bulkSetStatusMutation = useMutation({
    mutationFn: async () => {
      await runBulk('Set status', id =>
        api.patch(`/orders/${id}/status`, { status: bulkStatus }))
    },
  })

  const bulkReassignMutation = useMutation({
    mutationFn: async () => {
      await runBulk('Reassign', id =>
        api.put(`/orders/${id}`, { assignedTo: bulkWorker || null }))
    },
  })

  const bulkCancelMutation = useMutation({
    mutationFn: async () => {
      await runBulk('Cancel', id => api.post(`/orders/${id}/cancel`))
    },
    onSettled: () => setConfirmBulkCancel(false),
  })

  const handleBulkCancel = () => {
    if (selectedIds.size === 0) return
    setConfirmBulkCancel(true)
  }

  const bulkCancelCount = `${selectedIds.size} ${selectedIds.size === 1 ? 'order' : 'orders'}`

  const handleOpenCreateModal = () => {
    setEditingOrder(null)
    setModalOpen(true)
  }

  const handleOpenEditModal = (o: ManualOrder) => {
    setEditingOrder(o)
    setModalOpen(true)
  }

  // Never fall back to the raw id: a UUID in the Staff or Outlet cell reads as
  // data corruption to the user (the same rule as the category names on Inventory).
  const resolveWorkerName = (id?: string | null) => {
    if (!id) return 'Unassigned'
    return workers.find(w => w.id === id)?.name || 'Unknown staff'
  }

  // The order row carries its own storeName, for an outlet outside the loaded list.
  const resolveStoreName = (id?: string | null, rowName?: string | null) => {
    return (id && stores.find(s => s.id === id)?.name) || rowName || 'Unknown store'
  }

  const isSmall = width > 0 && width < 900
  const isPhone = useMediaQuery(PHONE_QUERY)
  const showBulk = canWriteOrders && selectedIds.size > 0

  const readyFromDefaultHint = useMemo(
    () => toLocalISOString(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).slice(0, 16),
    [],
  )

  // The seven panel filters minus "Hide appointments": that one is checked by
  // default, so counting it would always read at least one.
  const activeFilterCount = [
    filterStore, filterWorker, filterStatus, filterPayment, filterReadyFrom, filterReadyTo,
  ].filter(Boolean).length

  /** Empties the panel's filters. "Hide appointments" and the search box are left alone. */
  const clearFilters = () => {
    setFilterStore('')
    setFilterWorker('')
    setFilterStatus('')
    setFilterPayment('')
    setFilterReadyFrom('')
    setFilterReadyTo('')
  }

  // A cancel leaves the workflow status stale, so the row says Cancelled and is muted.
  const muted = (o: ManualOrder) => (isCancelledOrder(o) ? 'opacity-60' : '')

  const statusBadge = (o: ManualOrder) =>
    isCancelledOrder(o) ? <PaymentStatusBadge status="Cancelled" /> : <OrderStatusBadge status={o.status} />

  const overdueChip = (o: ManualOrder) =>
    isOrderOverdue(o) ? (
      <span className="inline-flex text-[10px] font-bold uppercase tracking-wide bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 px-2 py-0.5 rounded">
        Overdue
      </span>
    ) : null

  const columns: DataTableColumn<ManualOrder>[] = [
    ...(canWriteOrders
      ? [{
          id: 'select',
          header: (
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              aria-label="Select all orders on this page"
              data-testid="orders-select-all"
            />
          ),
          cell: (o: ManualOrder) => (
            <input
              type="checkbox"
              checked={selectedIds.has(o.id)}
              onChange={() => toggleOne(o.id)}
              aria-label={`Select ${o.orderNr}`}
            />
          ),
          width: 40,
        }]
      : []),
    {
      id: 'order',
      header: 'Order',
      cell: o => {
        const apptLine = formatOrderAppointmentLine(o.appointment)
        const calHref =
          o.appointment?.start
            ? calendarHrefForAppointment(o.appointment, o.id)
            : null
        return (
          <div className={muted(o)}>
            <Link
              to={`/orders/manage?orderId=${encodeURIComponent(o.id)}`}
              title="Open on the Board"
              className={`font-mono text-xs ${TOKENS.bgActive} ${TOKENS.textSecondary} px-2.5 py-1 rounded-lg border ${TOKENS.borderMuted} font-bold hover:underline`}
            >
              {o.orderNr}
            </Link>
            {o.planId ? (
              <div className="mt-1">
                <SubscriptionOrderChip order={o} />
              </div>
            ) : null}
            {apptLine && (
              <div className="mt-1">
                {calHref ? (
                  <a
                    href={calHref}
                    className="inline-flex text-[11px] font-semibold text-sky-600 dark:text-sky-400 hover:underline"
                    title="Open in Calendar"
                  >
                    {apptLine}
                  </a>
                ) : (
                  <span className={`text-[11px] font-semibold ${TOKENS.textMuted}`}>{apptLine}</span>
                )}
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'client',
      header: 'Client',
      cell: o => (
        <span className={`font-semibold ${TOKENS.textHeading} ${muted(o)}`}>{o.customerName ?? '—'}</span>
      ),
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: o => (
        <span className={`font-mono font-medium ${TOKENS.textHeading} ${muted(o)}`}>{formatMoney(o.amount)}</span>
      ),
    },
    {
      id: 'payment',
      header: 'Payment',
      cell: o => (
        <div className={muted(o)}>
          <PaymentStatusBadge status={o.paymentStatus} />
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: o => <div className={muted(o)}>{statusBadge(o)}</div>,
    },
    {
      id: 'staff',
      header: 'Staff and store',
      cell: o => (
        <div className={`space-y-0.5 text-xs ${muted(o)}`}>
          <div className="flex items-center gap-1">
            <User size={11} className="text-slate-400" />
            <span className="font-medium">{resolveWorkerName(o.assignedTo)}</span>
          </div>
          <div className={`flex items-center gap-1 text-[11px] ${TOKENS.textMuted}`}>
            <Store size={11} className="text-slate-400" />
            <span>{resolveStoreName(o.storeId, o.storeName)}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'target',
      header: 'Target',
      cell: o => (
        <div className={`space-y-1 ${muted(o)}`}>
          <div className={`flex items-center gap-1 text-xs ${TOKENS.textHeading} font-medium`}>
            <Clock size={12} className="text-slate-400" />
            <span>
              {formatOrderTargetLine({
                targets: o.targets,
                pickupAt: o.pickupAt,
                omitPrep: Boolean(o.appointment?.start || o.calendarBound),
              }) || '—'}
            </span>
          </div>
          {overdueChip(o)}
        </div>
      ),
    },
    ...(canWriteOrders
      ? [{
          id: 'actions',
          header: 'Actions',
          align: 'right' as const,
          cell: (o: ManualOrder) => (
            <button
              onClick={() => handleOpenEditModal(o)}
              className={`p-1.5 ${TOKENS.border} hover:${TOKENS.bgActive} ${TOKENS.textSecondary} hover:${TOKENS.textHeading} rounded-lg transition-all`}
              title="Modify Order"
            >
              <Edit2 size={13} />
            </button>
          ),
        }]
      : []),
  ]

  // Phones: the same order as a card, with the same filters applied.
  const renderCard = (o: ManualOrder) => (
    <div className={`space-y-3 ${muted(o)}`} data-testid="orders-card">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          {canWriteOrders && (
            <input
              type="checkbox"
              checked={selectedIds.has(o.id)}
              onChange={() => toggleOne(o.id)}
              aria-label={`Select ${o.orderNr}`}
            />
          )}
          <Link
            to={`/orders/manage?orderId=${encodeURIComponent(o.id)}`}
            title="Open on the Board"
            className={`font-mono text-xs ${TOKENS.bgActive} ${TOKENS.textSecondary} px-2 py-0.5 rounded border ${TOKENS.borderMuted} font-bold hover:underline`}
          >
            {o.orderNr}
          </Link>
          <SubscriptionOrderChip order={o} />
        </div>
        <div className="flex gap-2">
          <PaymentStatusBadge status={o.paymentStatus} />
          {canWriteOrders && (
            <button
              aria-label={`Edit order ${o.orderNr}`}
              onClick={() => handleOpenEditModal(o)}
              className={`p-1 ${TOKENS.border} hover:${TOKENS.bgActive} ${TOKENS.textSecondary} hover:${TOKENS.textHeading} rounded transition-all`}
            >
              <Edit2 size={12} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div>
          <h4 className={`font-semibold ${TOKENS.textHeading} text-sm`}>{o.customerName ?? '—'}</h4>
          <span className={`text-[10px] ${TOKENS.textMuted} block`}>Client</span>
        </div>
        <div className="text-right">
          <span className={`font-mono font-medium ${TOKENS.textHeading} text-sm`}>{formatMoney(o.amount)}</span>
          <span className={`text-[10px] ${TOKENS.textMuted} block`}>Amount</span>
        </div>
      </div>

      <div className={`flex justify-between items-center text-xs pt-2 border-t ${TOKENS.borderMuted}`}>
        <div className="flex flex-col gap-0.5">
          <span className={`text-[11px] uppercase tracking-wider ${TOKENS.textMuted}`}>Staff and store</span>
          <div className="flex items-center gap-1 font-medium text-slate-700 dark:text-zinc-300">
            <User size={10} />
            <span>{resolveWorkerName(o.assignedTo)}</span>
          </div>
          <div className={`flex items-center gap-1 text-[10px] ${TOKENS.textMuted}`}>
            <Store size={10} />
            <span>{resolveStoreName(o.storeId, o.storeName)}</span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1">
          {statusBadge(o)}
          <div className={`flex items-center gap-1 text-[10px] ${TOKENS.textMuted}`}>
            <Clock size={10} />
            <span>{formatOrderTargetLine({ targets: o.targets, pickupAt: o.pickupAt }) || '—'}</span>
          </div>
          {overdueChip(o)}
        </div>
      </div>
    </div>
  )

  const searchBox = (
    <div className="relative w-full md:max-w-xs">
      <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${TOKENS.textMuted}`} size={14} />
      <input
        type="text"
        placeholder="Search order or client..."
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        className={TOKENS.input}
      />
    </div>
  )

  // Everything in the filter card except the search box: on a narrow screen
  // these sit in the panel behind the "Filters" button.
  const filterControls = (
    <>
      <div className="flex items-center gap-2 text-xs">
        <span className={`font-semibold ${TOKENS.textMuted} uppercase tracking-wider`}>Store</span>
        <select
          value={filterStore}
          onChange={e => setFilterStore(e.target.value)}
          className={TOKENS.select}
          data-testid="orders-filter-store"
        >
          <option value="">All Stores</option>
          {stores.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <span className={`font-semibold ${TOKENS.textMuted} uppercase tracking-wider`}>Staff</span>
        <select
          value={filterWorker}
          onChange={e => setFilterWorker(e.target.value)}
          className={TOKENS.select}
          data-testid="orders-filter-staff"
        >
          <option value="">All Staff</option>
          {workers.map(w => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <span className={`font-semibold ${TOKENS.textMuted} uppercase tracking-wider`}>Status</span>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className={TOKENS.select}
          data-testid="orders-filter-status"
        >
          <option value="">All</option>
          {WORKFLOW_STATUSES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <span className={`font-semibold ${TOKENS.textMuted} uppercase tracking-wider`}>Pay</span>
        <select
          value={filterPayment}
          onChange={e => setFilterPayment(e.target.value)}
          className={TOKENS.select}
          data-testid="orders-filter-payment"
        >
          <option value="">All</option>
          <option value="Unpaid">Unpaid</option>
          {ORDER_PAYMENT_STATUSES.map(ps => (
            <option key={ps} value={ps}>{ps}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <span className={`font-semibold ${TOKENS.textMuted} uppercase tracking-wider`}>Pickup from</span>
        <input
          type="datetime-local"
          value={filterReadyFrom}
          onChange={e => setFilterReadyFrom(e.target.value)}
          className={TOKENS.input}
          data-testid="orders-filter-ready-from"
          placeholder={readyFromDefaultHint}
        />
      </div>

      <div className="flex items-center gap-2 text-xs">
        <span className={`font-semibold ${TOKENS.textMuted} uppercase tracking-wider`}>Pickup to</span>
        <input
          type="datetime-local"
          value={filterReadyTo}
          onChange={e => setFilterReadyTo(e.target.value)}
          className={TOKENS.input}
          data-testid="orders-filter-ready-to"
        />
      </div>

      <label
        className={`flex items-center gap-2 text-xs cursor-pointer select-none ${TOKENS.textHeading}`}
        title="Hides calendar-bound orders (services / slots). Needs quote stays visible."
      >
        <input
          type="checkbox"
          checked={hideCalendarBound}
          onChange={e => setHideCalendarBound(e.target.checked)}
          data-testid="orders-hide-appointments"
          className="rounded border-slate-300"
        />
        <span className="font-semibold uppercase tracking-wider">Hide appointments</span>
      </label>
    </>
  )

  return (
    <div ref={containerRef} className="space-y-6 p-4">
      <PageHeader
        title="List"
        subtitle="Review operational client invoices, payments, and worker assignments"
        actions={canWriteOrders && (
          <Button
            onClick={handleOpenCreateModal}
            icon={<Plus size={14} />}
            variant="primary"
            className="self-start"
          >
            New Order
          </Button>
        )}
      />

      {error && (
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-start gap-3 text-rose-600 dark:text-rose-400">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold block">Order update failed</span>
            <p className="mt-0.5 opacity-90">{error}</p>
          </div>
        </div>
      )}

      {isSmall ? (
        <div className="space-y-3" data-testid="orders-filter-bar">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">{searchBox}</div>
            <Button
              type="button"
              variant="secondary"
              icon={<SlidersHorizontal size={14} />}
              aria-expanded={filtersOpen}
              aria-controls={filtersPanelId}
              onClick={() => setFiltersOpen(open => !open)}
              className="shrink-0"
              data-testid="orders-filters-toggle"
            >
              {activeFilterCount > 0 ? `Filters · ${activeFilterCount}` : 'Filters'}
            </Button>
          </div>

          {filtersOpen && (
            <div
              id={filtersPanelId}
              className={`${TOKENS.bgCard} ${TOKENS.border} rounded-2xl p-4 shadow-sm space-y-3`}
              data-testid="orders-filters"
            >
              <div className="flex gap-4 flex-col">
                {filterControls}
              </div>
              {activeFilterCount > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={clearFilters}
                  className="w-fit"
                  data-testid="orders-filters-clear"
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={`${TOKENS.bgCard} ${TOKENS.border} rounded-2xl p-4 shadow-sm space-y-3`} data-testid="orders-filters">
          <div className="flex gap-4 flex-row flex-wrap items-end">
            {searchBox}
            {filterControls}
          </div>
        </div>
      )}

      {showBulk && (
        <div
          className={`flex flex-wrap items-center gap-3 px-4 py-3 ${TOKENS.bgCard} ${TOKENS.border} rounded-2xl sticky top-2 z-10`}
          data-testid="orders-bulk-actions"
        >
          <span className={`text-sm ${TOKENS.textSecondary}`}>
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            disabled={bulkBusy}
            onClick={() => setSelectedIds(new Set())}
            className={`text-xs font-semibold ${TOKENS.textMuted} hover:${TOKENS.textHeading}`}
          >
            Clear
          </button>

          <select
            value={bulkStatus}
            onChange={e => setBulkStatus(e.target.value)}
            className={TOKENS.select}
            disabled={bulkBusy}
            data-testid="orders-bulk-status"
          >
            {WORKFLOW_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={bulkBusy}
            onClick={() => bulkSetStatusMutation.mutate()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 disabled:opacity-40"
            data-testid="orders-bulk-status-apply"
          >
            Set status
          </button>

          <select
            value={bulkWorker}
            onChange={e => setBulkWorker(e.target.value)}
            className={TOKENS.select}
            disabled={bulkBusy}
            data-testid="orders-bulk-worker"
          >
            <option value="">Unassigned</option>
            {workers.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={bulkBusy}
            onClick={() => bulkReassignMutation.mutate()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 disabled:opacity-40"
            data-testid="orders-bulk-reassign-apply"
          >
            Reassign
          </button>

          <button
            type="button"
            disabled={bulkBusy}
            onClick={handleBulkCancel}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-40"
            data-testid="orders-bulk-cancel"
          >
            Cancel orders…
          </button>

          {bulkBusy && (
            <span className={`inline-flex items-center gap-1.5 text-xs ${TOKENS.textMuted}`}>
              <Loader2 size={13} className="animate-spin" />
              Working…
            </span>
          )}
          {bulkMessage && !bulkBusy && (
            <span className={`text-xs ${TOKENS.textSecondary}`}>{bulkMessage}</span>
          )}
        </div>
      )}

      {confirmBulkCancel && (
        <ConfirmDialog
          open
          tone="danger"
          title={`Cancel ${bulkCancelCount}?`}
          body={
            <>
              <p>
                {selectedIds.size === 1 ? 'Order' : 'Orders'} {listOrderNumbers(orders.filter(o => selectedIds.has(o.id)))}.
              </p>
              <p>You can't undo this from here.</p>
            </>
          }
          confirmLabel={`Cancel ${bulkCancelCount}`}
          cancelLabel={selectedIds.size === 1 ? 'Keep order' : 'Keep orders'}
          pending={bulkCancelMutation.isPending}
          onConfirm={() => bulkCancelMutation.mutate()}
          onCancel={() => setConfirmBulkCancel(false)}
        />
      )}

      {isPhone && canWriteOrders && orders.length > 0 && !ordersLoading && !ordersFailed && (
        <label className={`flex items-center gap-2 text-xs ${TOKENS.textSecondary} px-1`}>
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={toggleSelectAll}
            data-testid="orders-select-all"
          />
          Select all on page
        </label>
      )}

      <div
        aria-busy={showingPreviousPage || undefined}
        className={`transition-opacity ${showingPreviousPage ? 'opacity-60' : ''}`}
      >
        <DataTable
          label="Orders"
          columns={columns}
          rows={orders}
          rowKey={o => o.id}
          loading={ordersLoading}
          error={ordersFailed}
          errorSlot={
            <ErrorState
              title="Couldn't load orders"
              text={apiErrorMessage(ordersError)}
              onRetry={() => void refetchOrders()}
              retrying={ordersFetching}
            />
          }
          emptySlot={
            <EmptyState
              title="No orders match these filters"
              text={hideCalendarBound ? 'Visits are hidden. Untick Hide appointments to show them.' : undefined}
            />
          }
          renderCard={renderCard}
        />
        {!ordersLoading && !ordersFailed && (
          <Pagination
            page={pageIndex}
            totalPages={totalPages}
            totalElements={totalElements}
            isFirst={pageIndex === 0}
            isLast={pageIndex >= totalPages - 1}
            onPageChange={next => setPageParam(String(next + 1))}
          />
        )}
      </div>

      <CreateOrderModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editingOrder={editingOrder}
        onSuccess={() => setError(null)}
      />
    </div>
  )
}
